import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  DollarSign,
  Package,
  CheckCircle,
  BarChart3,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import apiService from '../services/api';

// Importar componentes personalizados
import FiltrosAvanzados from '../components/reportes/FiltrosAvanzados';
import KPICard from '../components/reportes/KPICard';
import DistribucionAlertas from '../components/reportes/DistribucionAlertas';
import HistogramaDemoras from '../components/reportes/HistogramaDemoras';
import ComparativaChart from '../components/reportes/ComparativaChart';
import TendenciaProyeccion from '../components/reportes/TendenciaProyeccion';
import ContenedoresRiesgo from '../components/reportes/ContenedoresRiesgo';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function Reportes() {
  const [filtros, setFiltros] = useState({
    fechaInicio: '',
    fechaFin: '',
    clientes: [],
    navieras: [],
    alertas: ['verde', 'amarillo', 'rojo'],
    facturacion: 'todos',
    compararPeriodo: false,
  });

  const [datos, setDatos] = useState({
    kpis: null,
    kpisComparativos: null,
    eficienciaOperativa: null,
    tendencias: [],
    proyeccion: [],
    distribucionAlertas: null,
    analisisDemoras: null,
    topClientes: [],
    topNavieras: [],
    contenedoresRiesgo: [],
  });

  const [ui, setUi] = useState({
    loading: true,
    error: null,
    modalExportacion: false,
  });

  useEffect(() => {
    fetchReportes();
  }, [filtros]);

  const fetchReportes = async () => {
    try {
      setUi(prev => ({ ...prev, loading: true, error: null }));

      const params = new URLSearchParams();

      // Fechas
      if (filtros.fechaInicio) params.append('fechaInicio', filtros.fechaInicio);
      if (filtros.fechaFin) params.append('fechaFin', filtros.fechaFin);

      // Clientes (array)
      if (filtros.clientes && filtros.clientes.length > 0) {
        filtros.clientes.forEach(id => params.append('clientes', id));
      }

      // Navieras (array)
      if (filtros.navieras && filtros.navieras.length > 0) {
        filtros.navieras.forEach(id => params.append('navieras', id));
      }

      // Alertas (array)
      if (filtros.alertas && filtros.alertas.length > 0) {
        filtros.alertas.forEach(alerta => params.append('alertas', alerta));
      }

      // Facturación
      if (filtros.facturacion && filtros.facturacion !== 'todos') {
        params.append('facturacion', filtros.facturacion);
      }

      // Comparar período
      if (filtros.compararPeriodo) params.append('compararConAnterior', 'true');

      // Fetch en paralelo todos los datos necesarios
      const [
        kpis,
        kpisComparativos,
        eficiencia,
        distribucion,
        demoras,
        tendencias,
        proyeccion,
        clientes,
        navieras,
        riesgo
      ] = await Promise.all([
        apiService.get(`/reportes/kpis?${params}`),
        filtros.compararPeriodo
          ? apiService.get(`/reportes/kpis-comparativa?${params}`)
          : Promise.resolve(null),
        apiService.get(`/reportes/eficiencia-operativa?${params}`),
        apiService.get(`/reportes/distribucion-alertas?${params}`),
        apiService.get(`/reportes/analisis-demoras?${params}`),
        apiService.get('/reportes/tendencias?meses=12'),
        apiService.get('/reportes/proyeccion?meses=3'),
        apiService.get(`/reportes/por-cliente?${params}&limit=10`),
        apiService.get(`/reportes/por-naviera?${params}`),
        apiService.get('/reportes/contenedores-riesgo?dias=7'),
      ]);

      setDatos({
        kpis,
        kpisComparativos,
        eficienciaOperativa: eficiencia,
        tendencias: proyeccion.historico || tendencias,
        proyeccion: proyeccion.proyectado || [],
        distribucionAlertas: distribucion,
        analisisDemoras: demoras,
        topClientes: clientes.data || clientes,
        topNavieras: navieras,
        contenedoresRiesgo: riesgo,
      });
    } catch (error) {
      console.error('Error al cargar reportes:', error);
      setUi(prev => ({ ...prev, error: 'Error al cargar los datos del reporte' }));
    } finally {
      setUi(prev => ({ ...prev, loading: false }));
    }
  };

  const handleExportar = async () => {
    try {
      const params = new URLSearchParams();

      // Fechas
      if (filtros.fechaInicio) params.append('fechaInicio', filtros.fechaInicio);
      if (filtros.fechaFin) params.append('fechaFin', filtros.fechaFin);

      // Clientes (array)
      if (filtros.clientes && filtros.clientes.length > 0) {
        filtros.clientes.forEach(id => params.append('clientes', id));
      }

      // Navieras (array)
      if (filtros.navieras && filtros.navieras.length > 0) {
        filtros.navieras.forEach(id => params.append('navieras', id));
      }

      // Alertas (array)
      if (filtros.alertas && filtros.alertas.length > 0) {
        filtros.alertas.forEach(alerta => params.append('alertas', alerta));
      }

      // Facturación
      if (filtros.facturacion && filtros.facturacion !== 'todos') {
        params.append('facturacion', filtros.facturacion);
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/reportes/exportar-excel?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error('Error al exportar:', error);
      alert('Error al exportar reporte');
    }
  };

  if (ui.loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-ink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-ink-900 mx-auto mb-4"></div>
          <p className="text-ink-600">Cargando reportes...</p>
        </div>
      </div>
    );
  }

  if (ui.error) {
    return (
      <div className="flex items-center justify-center h-screen bg-ink-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BarChart3 size={32} className="text-red-600" />
          </div>
          <p className="text-ink-900 font-semibold mb-2">Error al cargar reportes</p>
          <p className="text-ink-600 text-sm">{ui.error}</p>
          <button
            onClick={fetchReportes}
            className="mt-4 px-4 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-ink-50 min-h-screen">
      {/* Header */}
      <div className="mb-8 animate-fade-in">
        <h1 className="font-display text-3xl text-ink-900 mb-1">Reportes y Análisis</h1>
        <p className="text-ink-500">Métricas operativas y análisis predictivo</p>
      </div>

      {/* Filtros Avanzados */}
      <FiltrosAvanzados
        filtros={filtros}
        onChange={setFiltros}
        onExportar={handleExportar}
      />

      {/* KPIs Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KPICard
          titulo="Contenedores Activos"
          valor={datos.kpis?.totalContenedores}
          cambio={datos.kpisComparativos?.cambio?.contenedores}
          sparkline={datos.kpisComparativos?.sparklines?.contenedores}
          icono={Package}
          desglose={datos.kpis ? `${datos.kpis.totalDias} días totales` : null}
        />
        <KPICard
          titulo="Ingresos Totales"
          valor={datos.kpis?.ingresoTotal}
          cambio={datos.kpisComparativos?.cambio?.ingresos}
          sparkline={datos.kpisComparativos?.sparklines?.ingresos}
          formato="moneda"
          icono={DollarSign}
          desglose={datos.kpis ? `Costos: $${datos.kpis.costoTotal.toLocaleString('es-MX')}` : null}
        />
        <KPICard
          titulo="Utilidad Total"
          valor={datos.kpis?.utilidadTotal}
          cambio={datos.kpisComparativos?.cambio?.utilidad}
          formato="moneda"
          icono={TrendingUp}
          desglose={datos.kpis ? `Margen: ${datos.kpis.margenUtilidad}%` : null}
        />
        <KPICard
          titulo="Eficiencia Operativa"
          valor={datos.eficienciaOperativa?.pctVerdes}
          cambio={datos.eficienciaOperativa?.cambioVsAnterior}
          formato="porcentaje"
          icono={CheckCircle}
          desglose={datos.eficienciaOperativa ? `${datos.eficienciaOperativa.totalContenedores} contenedores` : null}
        />
      </div>

      {/* SECCIÓN ELIMINADA: Análisis Temporal
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-ink-900 mb-4">Análisis Temporal</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TendenciaProyeccion
            datos={datos.tendencias}
            proyeccion={datos.proyeccion}
          />
          {filtros.compararPeriodo && datos.kpisComparativos && (
            <ComparativaChart kpis={datos.kpisComparativos} />
          )}
        </div>
      </div>
      */}

      {/* SECCIÓN ELIMINADA: Análisis Operativo
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-ink-900 mb-4">Análisis Operativo</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DistribucionAlertas datos={datos.distribucionAlertas} />
          <HistogramaDemoras datos={datos.analisisDemoras} />
        </div>
        <div className="mt-6">
          <ContenedoresRiesgo contenedores={datos.contenedoresRiesgo} />
        </div>
      </div>
      */}

      {/* Tablas de Datos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tabla Clientes */}
        <div className="bg-white rounded-lg shadow-soft border border-ink-200 overflow-hidden animate-fade-in">
          <div className="p-6 border-b border-ink-200">
            <h3 className="text-lg font-semibold text-ink-900">
              Rentabilidad por Cliente
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-ink-50 border-b border-ink-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-600 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-ink-600 uppercase tracking-wider">
                    Cont.
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-ink-600 uppercase tracking-wider">
                    Días Prom.
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-ink-600 uppercase tracking-wider">
                    Utilidad
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-ink-600 uppercase tracking-wider">
                    Margen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {datos.topClientes.map((cliente, index) => (
                  <tr key={index} className="hover:bg-ink-50">
                    <td className="px-6 py-4 text-sm font-medium text-ink-900">
                      {cliente.cliente}
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-ink-700">
                      {cliente.totalContenedores}
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-ink-700">
                      {cliente.diasPromedio}
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-ink-900">
                      ${cliente.utilidad.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-ink-700">
                      {cliente.margen}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tabla Navieras */}
        <div className="bg-white rounded-lg shadow-soft border border-ink-200 overflow-hidden animate-fade-in">
          <div className="p-6 border-b border-ink-200">
            <h3 className="text-lg font-semibold text-ink-900">
              Actividad por Naviera
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-ink-50 border-b border-ink-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-600 uppercase tracking-wider">
                    Naviera
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-ink-600 uppercase tracking-wider">
                    Contenedores
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-ink-600 uppercase tracking-wider">
                    Días Prom.
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-ink-600 uppercase tracking-wider">
                    Costo Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {datos.topNavieras.map((naviera, index) => (
                  <tr key={index} className="hover:bg-ink-50">
                    <td className="px-6 py-4 text-sm font-medium text-ink-900">
                      {naviera.naviera}
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-ink-700">
                      {naviera.totalContenedores}
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-ink-700">
                      {naviera.promedioDias}
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-ink-900">
                      ${naviera.costoTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Reportes;
